import { Suspense, lazy, useEffect, useState } from 'react';
import type { ComponentType } from 'react';
import { Navigate, Route, Routes } from 'react-router';

import { SenzaRete } from './componenti/StatiGlobali';
import { Accesso } from './aree/accesso/Accesso';
import { Blocco } from './aree/accesso/Blocco';
import { PrimoAccesso } from './aree/accesso/PrimoAccesso';
import { SessioneScaduta } from './aree/accesso/SessioneScaduta';
import { useSessione } from './stato/Sessione';

import './stili/caratteri.css';
import './stili/buio.css';
import './stili/contrasto.css';
import './aree/accesso/accesso.css';

/*
 * Le tre aree si caricano quando servono.
 *
 * Prima stavano tutte in un fascio solo da 947 KB: un socio che apriva il proprio QR in
 * sala, dal telefono, scaricava anche le seimila righe dell'amministrazione e il lettore
 * di codici del Direttore di Tiro. Roba che non vedrà mai, sulla rete di un poligono, al
 * primo secondo dell'unica cosa che gli serve.
 *
 * I confini li disegnano questi `lazy`, e non un raggruppamento manuale: `vite.config.ts`
 * spiega perché il taglio per cartella era stato provato e tolto — assegnare un modulo a un
 * pezzo per percorso lo faceva risalire nel grafo iniziale, e in `index.html` finiva il
 * foglio dell'amministrazione, bloccante per chiunque. Ne escono una trentina di pezzi
 * invece di tre, ma richiesti uno alla volta e ciascuno col proprio CSS.
 *
 * L'accesso resta immediato: è la prima cosa che chiunque vede, e differirlo avrebbe
 * spostato l'attesa esattamente dove non serve. Per la stessa ragione i due moduli con cui
 * si apre un'area non passano più di qui ma da `AVVIO`, qui sotto: un `lazy` sul primo
 * disegno costa trecento millisecondi fissi, e il perché sta scritto lì.
 */
const Accettazioni = lazy(() => import('./aree/admin/Accettazioni').then((m) => ({ default: m.Accettazioni })));
const Avvisi = lazy(() => import('./aree/admin/Avvisi').then((m) => ({ default: m.Avvisi })));
const Calendario = lazy(() => import('./aree/admin/Calendario').then((m) => ({ default: m.Calendario })));
const Campi = lazy(() => import('./aree/admin/Configurazione').then((m) => ({ default: m.Campi })));
const Impostazioni = lazy(() => import('./aree/admin/Configurazione').then((m) => ({ default: m.Impostazioni })));
const OrariApertura = lazy(() => import('./aree/admin/Configurazione').then((m) => ({ default: m.OrariApertura })));
const Chiusure = lazy(() => import('./aree/admin/Chiusure').then((m) => ({ default: m.Chiusure })));
const Convalida = lazy(() => import('./aree/admin/Convalida').then((m) => ({ default: m.Convalida })));
const DettaglioTiratore = lazy(() => import('./aree/admin/DettaglioTiratore').then((m) => ({ default: m.DettaglioTiratore })));
const Eventi = lazy(() => import('./aree/admin/Eventi').then((m) => ({ default: m.Eventi })));
const Export = lazy(() => import('./aree/admin/Export').then((m) => ({ default: m.Export })));
const Guide = lazy(() => import('./aree/admin/Guide').then((m) => ({ default: m.Guide })));
const Ospiti = lazy(() => import('./aree/admin/Ospiti').then((m) => ({ default: m.Ospiti })));
const NuovoTiratore = lazy(() => import('./aree/admin/NuovoTiratore').then((m) => ({ default: m.NuovoTiratore })));
const PrenotazioniAdmin = lazy(() => import('./aree/admin/Prenotazioni').then((m) => ({ default: m.PrenotazioniAdmin })));
const Registro = lazy(() => import('./aree/admin/Registro').then((m) => ({ default: m.Registro })));
const Tiratori = lazy(() => import('./aree/admin/Tiratori').then((m) => ({ default: m.Tiratori })));
const UtenteAdmin = lazy(() => import('./aree/admin/Utente').then((m) => ({ default: m.UtenteAdmin })));
const Amministratori = lazy(() => import('./aree/admin/Utenze').then((m) => ({ default: m.Amministratori })));
const DirettoriDiTiro = lazy(() => import('./aree/admin/Utenze').then((m) => ({ default: m.DirettoriDiTiro })));
const Accettazione = lazy(() => import('./aree/dt/Accettazione').then((m) => ({ default: m.Accettazione })));
const Ospite = lazy(() => import('./aree/dt/Ospite').then((m) => ({ default: m.Ospite })));
const PrenotazioniDt = lazy(() => import('./aree/dt/Prenotazioni').then((m) => ({ default: m.PrenotazioniDt })));
const Sessioni = lazy(() => import('./aree/dt/Sessioni').then((m) => ({ default: m.Sessioni })));
const Utente = lazy(() => import('./aree/dt/Utente').then((m) => ({ default: m.Utente })));
const Armi = lazy(() => import('./aree/tiratore/Armi').then((m) => ({ default: m.Armi })));
const Benvenuto = lazy(() => import('./aree/tiratore/Benvenuto').then((m) => ({ default: m.Benvenuto })));
const Prenota = lazy(() => import('./aree/tiratore/Prenota').then((m) => ({ default: m.Prenota })));
const Profilo = lazy(() => import('./aree/tiratore/Profilo').then((m) => ({ default: m.Profilo })));
const Qr = lazy(() => import('./aree/tiratore/Qr').then((m) => ({ default: m.Qr })));
const Storico = lazy(() => import('./aree/tiratore/Storico').then((m) => ({ default: m.Storico })));
const PaginaSituazione = lazy(() => import('./aree/situazione/PaginaSituazione').then((m) => ({ default: m.PaginaSituazione })));
const PaginaOspite = lazy(() => import('./aree/ospite/PaginaOspite').then((m) => ({ default: m.PaginaOspite })));
const SistemaVisivo = lazy(() => import('./dev/SistemaVisivo').then((m) => ({ default: m.SistemaVisivo })));
const ProvaSituazione = lazy(() => import('./dev/ProvaSituazione').then((m) => ({ default: m.ProvaSituazione })));


/*
 * I due moduli con cui si apre un'area, caricati **fuori** da `Suspense`.
 *
 * ── i trecento millisecondi che nessuna ottimizzazione spiegava
 *
 * Misurando l'avvio: `/auth/me` risolta a 23 ms, tutti i pezzi scaricati a 58 ms,
 * processore fermo, rete ferma — e la prima schermata disegnata a 345 ms. Non c'era lavoro
 * da nessuna parte, in mezzo, per trecento millisecondi buoni.
 *
 * È React. Quando un `lazy` sospende, il ripiego di `Suspense` viene disegnato; e per non
 * farlo lampeggiare React trattiene la rivelazione del contenuto vero fino a 300 ms dopo
 * (`FALLBACK_THROTTLE_MS`). Su una navigazione interna il ripiego non fa in tempo a
 * comparire e non si paga niente. All'avvio invece si paga sempre, perché il ripiego c'è
 * per forza: `lazy` sospende **la prima volta che viene letto**, anche quando il modulo è
 * già in memoria — provato importandolo e attendendolo prima di `createRoot`: 346 ms
 * identici.
 *
 * ── perché non basta togliere il `lazy`
 *
 * Perché renderebbe statiche tutte e tre le aree: +24 kB compressi di JavaScript e +7 kB di
 * CSS per chiunque, con i fogli d'area di nuovo bloccanti — che è esattamente il difetto
 * descritto in `vite.config.ts`. Un socio col telefono tornerebbe a scaricare
 * l'amministrazione.
 *
 * ── quel che si fa invece
 *
 * Si carica a mano, con un `import()`, la coppia di moduli della **sola** area che serve, e
 * finché non è pronta si disegna l'attesa come componente normale. Nessun `Suspense`
 * coinvolto, quindi nessun ripiego e nessuna soglia: si disegna appena il modulo c'è.
 * I pezzi restano tre e i fogli restano per area — non cambia niente nel `dist`, cambia
 * soltanto chi aspetta chi.
 *
 * Tutto il resto dell'applicazione resta `lazy`, ed è giusto: dalla seconda schermata in
 * poi il ripiego non compare e la soglia non si applica.
 */
interface ModuliAvvio {
  ruolo: string;
  /** Il guscio dell'area: barra, testata, `Outlet`. */
  Contenitore: ComponentType;
  /** Che cosa si vede per primo dentro il guscio. */
  Prima: ComponentType;
}

const AVVIO: Record<string, () => Promise<Omit<ModuliAvvio, 'ruolo'>>> = {
  tiratore: async () => {
    const [guscio, benvenuto] = await Promise.all([
      import('./aree/tiratore/Guscio'),
      import('./aree/tiratore/Benvenuto'),
    ]);
    return { Contenitore: guscio.Guscio, Prima: benvenuto.Apertura };
  },
  dt: async () => {
    const [guscio, scansione] = await Promise.all([
      import('./aree/dt/GuscioDt'),
      import('./aree/dt/Scansione'),
    ]);
    return { Contenitore: guscio.GuscioDt, Prima: scansione.Scansione };
  },
  admin: async () => {
    const [guscio, panoramica] = await Promise.all([
      import('./aree/admin/GuscioAdmin'),
      import('./aree/admin/Panoramica'),
    ]);
    return { Contenitore: guscio.GuscioAdmin, Prima: panoramica.Panoramica };
  },
};

/**
 * I moduli dell'area di chi è collegato, o `null` finché non ci sono.
 *
 * Il ruolo nel valore restituito, e non solo nella dipendenza dell'effetto: cambiando veste
 * si passa da un'area all'altra, e senza il confronto si disegnerebbe per un istante il
 * guscio di prima dentro le rotte di adesso.
 */
function useAvvio(ruolo: string | null): ModuliAvvio | null {
  const [moduli, setModuli] = useState<ModuliAvvio | null>(null);

  useEffect(() => {
    if (!ruolo || !AVVIO[ruolo]) return;
    let vivo = true;
    void AVVIO[ruolo]().then((m) => {
      if (vivo) setModuli({ ruolo, ...m });
    });
    return () => {
      vivo = false;
    };
  }, [ruolo]);

  return moduli?.ruolo === ruolo ? moduli : null;
}


/**
 * Le rotte che non passano dallo stato della sessione.
 *
 * Tutto il resto dell'applicazione decide che cosa mostrare guardando `stato` prima
 * ancora dell'indirizzo: in caricamento una schermata vuota, senza rete l'invito a
 * riprovare, scaduta la 6i, anonimo l'accesso. Per una pagina pubblica quel controllo è
 * di troppo — chi apre la situazione dei campi non ha una sessione da caricare, e a chi
 * ne ha una scaduta non si deve dire «sessione scaduta» su un indirizzo aperto a tutti.
 */
export function App() {
  return (
    /*
     * Un confine solo, il più esterno possibile.
     *
     * Metterne uno per rotta avrebbe fatto lampeggiare l'intelaiatura a ogni passaggio fra
     * due schermate della stessa area — che è già in memoria e non ha niente da aspettare.
     * Qui l'attesa si vede una volta per area, la prima volta che ci si entra.
     */
    <Suspense fallback={<Attesa />}>
      <Routes>
      {/*
        L'indirizzo aperto a chiunque: nessuna sessione, nessun nome. Sta qui e non dentro
        `Applicazione` perché lì la prima domanda è sempre «che sessione hai», e a chi apre
        la situazione dei campi quella domanda non si applica — né si può rispondergli
        «sessione scaduta» su una pagina che non ne ha mai chiesta una.
      */}
      <Route path="/situazione" element={<PaginaSituazione conNomi={false} conBuio />} />
      {/*
        R-51 — il modulo con cui un ospite scrive da sé i propri dati.

        Sta qui accanto alla situazione dei campi e per la stessa ragione: chi lo apre non
        ha una sessione e non deve averla, e la prima domanda di `Applicazione` — «che
        sessione hai» — a lui non si applica. Dire «sessione scaduta» a uno sconosciuto che
        apre l'indirizzo stampato sulla locandina all'ingresso sarebbe, oltre che falso, il
        modo più rapido di rimandarlo alla coda che questa pagina esiste per accorciare.
      */}
      <Route path="/ospite" element={<PaginaOspite />} />
      <Route path="/sistema-visivo" element={<SistemaVisivo />} />
      {/*
        Banco di prova, fuori dalla build di produzione.
        Non è pudore: quella pagina monta il gancio della dashboard e interroga il server
        ogni trenta secondi: pubblicarla significa un'interrogazione in più ogni mezzo
        minuto per chiunque ci capiti, per una schermata che serve solo a chi la disegna.
        `/sistema-visivo` resta com'era — è un catalogo statico, non chiama nulla.
      */}
      {import.meta.env.DEV ? (
        <Route path="/prova-situazione" element={<ProvaSituazione />} />
      ) : null}
        <Route path="*" element={<Applicazione />} />
      </Routes>
    </Suspense>
  );
}

/**
 * L'applicazione dietro l'accesso.
 *
 * L'accesso resta la sola porta; da lì si dirama negli stati trasversali (primo accesso,
 * schermo bloccato, sessione scaduta, rete assente) e poi nell'area di competenza del
 * ruolo.
 */
function Applicazione() {
  const { stato, profilo, bloccata, aggiorna } = useSessione();
  // In cima, prima delle uscite anticipate: l'ordine dei ganci non ammette rami.
  const avvio = useAvvio(profilo?.ruolo ?? null);

  if (stato === 'caricamento') return <Attesa />;
  if (stato === 'senza_rete') return <SenzaRete onRiprova={() => void aggiorna()} />;
  if (stato === 'scaduta') return <SessioneScaduta />;

  if (stato === 'anonimo' || !profilo) {
    return (
      <Routes>
        <Route path="*" element={<Accesso />} />
      </Routes>
    );
  }

  // Finché la password del PDF non è sostituita, ogni rotta protetta riporta qui.
  if (profilo.primo_accesso) return <PrimoAccesso />;

  // L'area non è ancora arrivata: la stessa attesa di prima, ma senza `Suspense` di mezzo.
  if (!avvio) return <Attesa />;

  const { Contenitore, Prima } = avvio;

  return (
    <>
      <Routes>
        {profilo.ruolo === 'tiratore' ? (
          <Route element={<Contenitore />}>
            {/* `Apertura` sceglie fra benvenuto e Home senza cambiare rotta. */}
            <Route index element={<Prima />} />
            <Route path="/benvenuto" element={<Benvenuto />} />
            <Route path="/armi" element={<Armi />} />
            <Route path="/qr" element={<Qr />} />
            <Route path="/prenota" element={<Prenota />} />
            <Route path="/storico" element={<Storico />} />
            <Route path="/area/situazione" element={<PaginaSituazione conNomi={false} incassata />} />
            <Route path="/profilo" element={<Profilo />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        ) : profilo.ruolo === 'dt' ? (
          <Route path="/dt" element={<Contenitore />}>
            <Route index element={<Prima />} />
            <Route path="accettazione" element={<Accettazione />} />
            {/* R-30 — l'uscita è una scansione, e si registra nella schermata stessa. */}
            <Route path="ospite" element={<Ospite />} />
            <Route path="sessioni" element={<Sessioni />} />
            <Route path="prenotazioni" element={<PrenotazioniDt />} />
            <Route path="situazione" element={<PaginaSituazione conNomi incassata />} />
            <Route path="utente" element={<Utente />} />
          </Route>
        ) : (
          <Route path="/admin" element={<Contenitore />}>
            {/*
              La Panoramica è la porta d'ingresso: le cose da fare oggi, i campi adesso e
              quel che viene nei prossimi giorni. Prima si entrava nell'elenco dei tiratori.
              L'ordine qui sotto è quello delle sezioni del menù (`navigazione.tsx`).
            */}
            <Route index element={<Navigate to="/admin/panoramica" replace />} />
            <Route path="panoramica" element={<Prima />} />
            {/* La dashboard per il secondo monitor della segreteria: resta com'era. */}
            <Route
              path="situazione"
              element={<PaginaSituazione conNomi incassata conBuio conDettagli />}
            />
            <Route path="prenotazioni" element={<PrenotazioniAdmin />} />
            <Route path="convalida" element={<Convalida />} />

            {/* Il mese intero: chiusure, corsi, prenotazioni e presenze in una vista. */}
            <Route path="calendario" element={<Calendario />} />
            {/* R-46 — corsi e allenamenti: i campi che il poligono tiene per sé. */}
            <Route path="eventi" element={<Eventi />} />
            <Route path="chiusure" element={<Chiusure />} />

            <Route path="tiratori" element={<Tiratori />} />
            <Route path="tiratori/nuovo" element={<NuovoTiratore />} />
            <Route path="tiratori/:id" element={<DettaglioTiratore />} />
            {/* R-52 — gli ospiti che la segreteria registra allo sportello. */}
            <Route path="ospiti" element={<Ospiti />} />
            <Route path="direttori" element={<DirettoriDiTiro />} />
            <Route path="amministratori" element={<Amministratori />} />

            <Route path="accettazioni" element={<Accettazioni />} />
            <Route path="registro" element={<Registro />} />
            <Route path="export" element={<Export />} />

            <Route path="orari" element={<OrariApertura />} />
            <Route path="campi" element={<Campi />} />
            <Route path="avvisi" element={<Avvisi />} />
            <Route path="impostazioni" element={<Impostazioni />} />
            <Route path="guide" element={<Guide />} />

            <Route path="utente" element={<UtenteAdmin />} />

            {/*
              Gli indirizzi di prima, per i segnalibri e per chi li aveva imparati.

              «Utenze» era la schermata unica di amministratori e Direttori di Tiro: porta ai
              DT, la schiera più numerosa. «Turni» era «Turni e chiusure»: porta agli orari,
              che ne erano la prima metà.
            */}
            <Route path="utenze" element={<Navigate to="/admin/direttori" replace />} />
            <Route path="turni" element={<Navigate to="/admin/orari" replace />} />
          </Route>
        )}
        {profilo.ruolo === 'dt' ? (
          <Route path="*" element={<Navigate to="/dt" replace />} />
        ) : profilo.ruolo === 'admin' ? (
          <Route path="*" element={<Navigate to="/admin/panoramica" replace />} />
        ) : null}
      </Routes>
      {bloccata ? <Blocco /> : null}
    </>
  );
}

function Attesa() {
  return (
    <div
      style={{
        minHeight: '100dvh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--testo-terziario)',
      }}
    >
      <span className="solo-lettore-schermo">Caricamento</span>
    </div>
  );
}
